import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  UserPlus, Check, AlertCircle, CircleDashed, Lock, HelpCircle, ExternalLink,
  RefreshCw, Trash2, ChevronDown, ChevronRight, MinusCircle, ClipboardCheck, X, Pencil,
} from 'lucide-react';

// ── Onboarding ────────────────────────────────────────────────────────────────
// "Bring this person on" as a task somebody accepts and works down in order.
//
// Paul sets the task, Jayda accepts it, and the eight steps run in the order they
// have to happen. The engine already knows whether most of them are done — the driver
// row, the rates, the route, the vehicle, the two contracts all leave evidence — so a
// step reads as done because the thing HAPPENED, not because someone remembered to
// tick it. Every step carries a Help button with what to do, where, and what "done"
// looks like, plus the traps that catch people on that particular step.
//
// Evidence comes from three places, all of them the same source the target page uses:
//   • onboarding-sb        — driver row, routes, fleet, both contract registers
//   • driver-dms-sb        — document statuses (the Compliance page's own call)
//   • app context sheetData — service rates, which live on the Rates sheet
// Nothing is re-implemented here; if a rule changes on its own page it changes here.

const API = '/.netlify/functions/onboarding-sb';
const DMS_API = '/.netlify/functions/driver-dms-sb';

const post = async (url, body) => {
  const res = await fetch(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  const text = await res.text();
  let j;
  try { j = JSON.parse(text); } catch { throw new Error(`The server took too long to reply (HTTP ${res.status}).`); }
  if (!j.success) throw new Error(j.error || 'Request failed');
  return j;
};

// ── styles (same idiom as the other admin pages) ──────────────────────────────
const card = { border: '1px solid var(--hp-border)', borderRadius: 10, background: 'var(--hp-surface)' };
const btn = { padding: '7px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnSm = { ...btn, padding: '4px 9px', fontSize: '0.78rem' };
const btnPrimary = { ...btn, background: 'var(--hp-gold)', color: '#000', border: '1px solid var(--hp-gold)', fontWeight: 600 };
const field = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const label = { fontSize: '0.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 5 };
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
const dim = { color: 'var(--hp-dim)' };

// ── step status vocabulary ────────────────────────────────────────────────────
// done     — the evidence says it happened
// progress — started but not finished (agreement sent, some documents in)
// todo     — nothing yet; this is what "required" looks like
// blocked  — can't start: it needs the login, and there isn't one yet
// na       — deliberately marked not applicable (his own van needs no lease)
const STATE = {
  done:     { label: 'Done',      fg: '#16a34a', bg: 'rgba(34,197,94,0.14)',  Icon: Check },
  progress: { label: 'Started',   fg: '#b45309', bg: 'rgba(245,158,11,0.16)', Icon: CircleDashed },
  todo:     { label: 'Required',  fg: '#b45309', bg: 'rgba(245,158,11,0.16)', Icon: AlertCircle },
  blocked:  { label: 'Waiting',   fg: '#64748b', bg: 'rgba(148,163,184,0.18)', Icon: Lock },
  na:       { label: 'Not needed',fg: '#64748b', bg: 'rgba(148,163,184,0.18)', Icon: MinusCircle },
};

function Pill({ state }) {
  const s = STATE[state] || STATE.todo;
  const I = s.Icon;
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600,
      background: s.bg, color: s.fg, whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 5,
    }}>
      <I size={12} /> {s.label}
    </span>
  );
}

const money = (n) => (n || n === 0) ? `$${Number(n).toFixed(3)}` : '—';

// ── the eight steps ───────────────────────────────────────────────────────────
// Order is Paul's. `resolve` reads the evidence and says where the step stands;
// `help` is what the Help button opens — what to do, where, and what catches people.
const STEPS = [
  {
    key: 'login',
    title: 'Set his portal login name and PIN',
    where: 'Right here — no need to go anywhere else',
    // With no login yet the work happens right here, so there is nowhere to send her.
    link: (t) => t.driverLogin ? `/drivers?q=${encodeURIComponent(t.driverLogin)}` : '',
    linkLabel: (t) => t.driverLogin ? 'Open his record' : '',
    needsLogin: false,
    resolve: (ev, t) => {
      if (!t.driverLogin) return { state: 'todo', detail: 'No login linked to this task yet.' };
      if (!ev.driver.exists) return { state: 'todo', detail: `No driver ${t.driverLogin} in the engine yet.` };
      if (!ev.driver.hasPin) return { state: 'progress', detail: 'Driver record exists, but no portal PIN is set.' };
      const bits = [ev.driver.name || 'no name on record', ev.driver.active ? 'active' : 'INACTIVE', 'PIN set'];
      if (ev.driver.type) bits.push(`type ${ev.driver.type}`);
      return { state: ev.driver.active ? 'done' : 'progress', detail: bits.join(' · ') };
    },
    help: [
      ['Where the login comes from', 'Australia Post issues it in the CMS portal. We do not invent it. Copy it exactly, character for character — it has to match the login he uses on the handheld, because every parcel he scans is filed under that string.'],
      ['Create the record here', 'Press "Create his driver record" on this step. It asks for the few things a record needs to exist — login, name, mobile, route — creates him, and attaches him to this task in one go. You do not need to visit Personnel.'],
      ['What you do NOT need yet', 'Company name, bank details, insurance, rates, streets. Steps 2 to 8 ask for those, and step 2 asks HIM for most of them. Do not hold up creating the record for things you have not been given.'],
      ['If Paul already added him', 'Use "Already created him? Link the login" and type the login. Same result.'],
      ['Sole trader is fine to start', "An ABN is required; a company is not. Setting a company up takes weeks and we don't hold anyone up for it, so most people start on their own sole-trader ABN and move to a company later. Leave Company Name blank if he hasn't got one — but never leave the ABN blank, because the RCTI needs it."],
      ['Trap — the start date', 'The engine stamps the commencement date as the day you press Save, not his actual start date. If you create him early, tell Paul so it can be corrected.'],
      ['The PIN', 'Leave it blank and the engine generates one, then shows it to you — write it down and give it to him with his login. If you lose it: People → Drivers → the edit button on his row reveals and resets it.'],
    ],
    done: 'He has a driver record, marked active, with a portal PIN — and the login is linked to this task.',
  },
  {
    key: 'documents',
    title: 'Request the documents he has not supplied',
    where: 'Compliance & Docs → Compliance → Requirements tab',
    link: (t) => `/compliance?tab=requirements&driver=${encodeURIComponent(t.driverLogin)}`,
    linkLabel: () => 'Open Requirements',
    needsLogin: true,
    resolve: (ev, t, x) => {
      const d = x.docs;
      if (!d) return { state: 'todo', detail: 'Compliance record not loaded.' };
      const total = d.required.length;
      if (!total) return { state: 'todo', detail: 'No document set chosen for him yet.' };
      const supplied = d.required.filter((k) => d.items[k]?.status === 'ok').length;
      const asked = d.required.filter((k) => d.items[k]?.status === 'renewal').length;
      const detail = `${supplied} of ${total} on file` + (asked ? ` · ${asked} requested and waiting` : '') +
        (ev.docsRequirementSet ? '' : ' · using the default set, not one you chose');
      if (supplied >= total) return { state: 'done', detail };
      if (asked || supplied) return { state: 'progress', detail };
      return { state: 'todo', detail };
    },
    help: [
      ['What this screen shows you', 'Pick him in the personnel list and you see every document type with what he has already supplied — a green tick against the ones on file, and the missing ones called out. The ticks below on this task are the same data.'],
      ['Choose his set, then push it', 'Tick every document he must supply, press Save, then Push to portal. He gets a red dot on My Docs and the daily sweep chases him until they are in.'],
      ['The standard set for an Australian driver', "Australian Driver's Licence · Australia Post ID card · passport or birth certificate / citizenship · vehicle insurance · vehicle registration · workers compensation policy · company details · emergency contacts · primary address · scanner asset number."],
      ['Leave the visa items unticked', 'For someone with unrestricted work rights there is no visa, CoE, letter of enrolment or study pack. Ticking them leaves him permanently non-compliant on the board for documents he can never supply.'],
      ['Why the ticked set matters', 'Every count on the Compliance page — "needs attention", "fully compliant" — is measured against exactly the set you tick here. Tick what he really owes and nothing else.'],
    ],
    done: 'His document set is chosen, pushed to his portal, and showing as requested.',
  },
  {
    key: 'rates',
    title: 'Set his prices',
    where: 'Pay & Billing → Driver Review → rate editor',
    link: (t) => `/driver-review?driver=${encodeURIComponent(t.driverLogin)}`,
    linkLabel: () => 'Open Driver Review',
    needsLogin: true,
    resolve: (ev, t, x) => {
      // The rate card lives on the Rates sheet, which the app loads once at start-up.
      // "Still loading" and "no rate set" have to read differently — one is a wait, the
      // other is a driver who would bill nothing.
      if (x.ratesLoading) return { state: 'blocked', detail: 'Reading the rate card…' };
      if (x.ratesUnavailable) return { state: 'blocked', detail: 'The rate card did not load — open Driver Review to check his rates there.' };
      const r = x.rates;
      if (!r) return { state: 'todo', detail: 'No rate card row for him — he would bill nothing.' };
      const parcel = Number(r.Parcel) || 0;
      const exp = Number(r.Exp) || 0;
      if (!parcel && !exp) return { state: 'todo', detail: 'No parcel or express rate on file — he would bill nothing.' };
      const detail = `Parcel ${money(r.Parcel)} · Express ${money(r.Exp)}` +
        (r.DOE ? ` · DoE ${money(r.DOE)}` : '') + (r['Depot Bags'] ? ` · Depot ${money(r['Depot Bags'])}` : '');
      if (!parcel || !exp) return { state: 'progress', detail: `${detail} — one of them is still unset.` };
      return { state: 'done', detail };
    },
    help: [
      ['A new starter has two stages', 'A training week paid hourly, then parcel rates. They are two different machines in the engine and you have to switch him between them — this step is the parcel rates.'],
      ['The training week', 'Personnel → his record → Type = Hourly. Then CTTCD Report → Award Rates → his row → set the minimum hourly rate and his van size. He then shows on Hourly Drivers and bills hours × rate.'],
      ['The parcel rates', 'Personnel → Type = Parcel. Then Driver Review → pick him → rate editor → enter Parcel, Express, DoE and Depot. Activate them from his first parcel day so nothing before that date re-prices.'],
      ['Copy from the drivers already on his round', 'Open each of them in Driver Review and read their current deal. Do not retype from memory — a wrong third decimal place is thousands of parcels.'],
      ['Check the rate against the CTTCD floor', 'The minimum for a contract driver is $37.80/hr car or micro, $39.15 small van, $41.04 medium, $43.74 large. Anything below the floor for his van is Paul’s call, not yours — and setting a low minimum hourly rate also switches off the top-up the engine would otherwise work out.'],
      ['A change later is a new activation', 'If a rate changes after he has been billed, it is activated from the new date. Never edit a rate that has already paid a period.'],
    ],
    done: 'A parcel rate and an express rate are on file, activated from his first parcel day.',
  },
  {
    key: 'routes',
    title: 'Assign his streets',
    where: 'Parcels & Routes → Driver Routes → Routes / Transfer',
    link: (t) => `/driver-routes?tab=routes&driver=${encodeURIComponent(t.driverLogin)}`,
    linkLabel: () => 'Open Driver Routes',
    needsLogin: true,
    resolve: (ev) => {
      const { rows, streets, codes, codesComplete } = ev.routes;
      if (!rows) return { state: 'todo', detail: 'No route row — the depot floor plan and the sorters do not know he exists.' };
      if (!streets) return { state: 'progress', detail: 'Route row exists but carries no streets.' };
      const c = codes || {};
      const codeBits = [c.dNumber && `D ${c.dNumber}`, c.bay && `bay ${c.bay}`, c.chute && `chute ${c.chute}`, c.wave && `wave ${c.wave}`, c.outlet && `outlet ${c.outlet}`].filter(Boolean).join(' · ');
      const detail = `${streets} street${streets === 1 ? '' : 's'}` + (codeBits ? ` · ${codeBits}` : '');
      if (!codesComplete) return { state: 'progress', detail: `${detail} — depot codes incomplete` };
      return { state: 'done', detail };
    },
    help: [
      ['Find the round first', 'On the Routes tab, find the round he is joining and see who holds those streets today. You are usually splitting an existing round, not inventing one.'],
      ['Move the streets', 'Use the Transfer tab to move streets from the driver who holds them to him, or "Add Street to This Route" for a street that is new to us.'],
      ['Fill the depot codes', 'ADAM code, D number, chute, bay, wave and outlet on his row. The Depot Floor Plan and the night sorters’ Sort Guide read these — without them the sorters have nowhere to put his parcels. This step does not read as done until they are in.'],
      ['Do not tick "AP notified"', 'Every street move writes a Change Log entry flagged as not yet notified to Australia Post. Sending that notification is Paul’s. Leave the flag alone.'],
    ],
    done: 'He has his own route row with streets and complete depot codes, and the change is sitting in the Change Log for Paul.',
  },
  {
    key: 'compliance',
    title: 'Check his compliance items again',
    where: 'Compliance & Docs → Compliance',
    link: (t) => `/compliance?driver=${encodeURIComponent(t.driverLogin)}`,
    linkLabel: () => 'Open Compliance',
    needsLogin: true,
    resolve: (ev, t, x) => {
      const d = x.docs;
      if (!d) return { state: 'todo', detail: 'Compliance record not loaded.' };
      const total = d.required.length;
      if (!total) return { state: 'blocked', detail: 'Choose his document set in step 2 first.' };
      const bad = d.required.filter((k) => ['missing', 'expired'].includes(d.items[k]?.status));
      const soon = d.required.filter((k) => ['expiring', 'renewal'].includes(d.items[k]?.status));
      if (bad.length) return { state: 'todo', detail: `${bad.length} still missing or expired of ${total}.` };
      if (soon.length) return { state: 'progress', detail: `All supplied — ${soon.length} expiring or awaiting the office.` };
      return { state: 'done', detail: `All ${total} required documents on file and current.` };
    },
    help: [
      ['This is the second look, on purpose', 'Step 2 asks him for the documents. This step is you checking, a few days later, what actually arrived. A request is not a document.'],
      ['What to do with what came in', 'Open his drawer on the Compliance page. Anything he uploaded sits waiting for the office to acknowledge — read it, check the expiry the engine picked up matches the document, and acknowledge it.'],
      ['Chase what has not arrived', 'Each outstanding item has a reminder button that re-sends by SMS and email. Use it rather than making a note to ring him.'],
      ['He cannot start without these', 'Licence, insurance, registration and workers compensation are the ones that stop him working, not just the ones that look untidy on the board.'],
    ],
    done: 'Every document in his required set is on file and current.',
  },
  {
    key: 'vehicle',
    title: 'Assign a vehicle',
    where: 'Vehicles → Fleet',
    link: (t) => `/fleet?driver=${encodeURIComponent(t.driverLogin)}`,
    linkLabel: () => 'Open Fleet',
    needsLogin: true,
    resolve: (ev) => {
      const v = ev.vehicle;
      if (!v.assigned && v.personnelRego) {
        return { state: 'progress', detail: `${v.personnelRego} is typed on his personnel record but the fleet register has no vehicle against him.` };
      }
      if (!v.assigned) return { state: 'todo', detail: 'No vehicle against his login in the fleet register.' };
      if (v.mismatch) return { state: 'progress', detail: `Fleet says ${v.rego}, his personnel record says ${v.personnelRego} — one of them is wrong.` };
      return { state: 'done', detail: `${v.rego}${v.describe ? ` · ${v.describe}` : ''}` };
    },
    help: [
      ['Which van', 'Vehicles → Fleet. Assign the rego to his login. If he is taking one of ours it comes with a lease (step 7); if he supplies his own, record it here anyway so fines, tolls and swaps have somebody to land on.'],
      ['The fleet register is the record', 'A rego typed onto the personnel record is not an assignment — it is a note. The "who was driving?" lookup, the fines register and the vehicle swap history all read the fleet register.'],
      ['Check the vehicle documents', 'Registration and insurance for the van belong on the vehicle, not just on him. A van with expired rego stops the round regardless of whose name is on it.'],
    ],
    done: 'The fleet register shows a rego against his login, and it matches his personnel record.',
  },
  {
    key: 'lease',
    title: 'Complete the vehicle lease agreement',
    where: 'Compliance & Docs → Contracts → Vehicle Lease Agreements',
    link: (t) => `/contracts?tab=vehicle-lease&driver=${encodeURIComponent(t.driverLogin)}`,
    linkLabel: () => 'Open Vehicle Lease',
    needsLogin: true,
    resolve: (ev) => {
      const c = ev.lease;
      if (c.status === 'signed') return { state: 'done', detail: `Signed${c.rego ? ` · ${c.rego}` : ''}` };
      if (c.status === 'pending') return { state: 'progress', detail: `Sent${c.rego ? ` for ${c.rego}` : ''} — waiting for him to sign in the portal.` };
      if (c.status === 'declined') return { state: 'todo', detail: 'He declined the lease — talk to Paul before re-issuing.' };
      if (!ev.vehicle.assigned) return { state: 'todo', detail: 'No lease on file. If he supplies his own van, mark this step not needed.' };
      return { state: 'todo', detail: 'No lease generated yet.' };
    },
    help: [
      ['Only if the van is ours', 'This is the Hollypaul Leasing agreement for a van we supply. If he drives his own vehicle there is no lease — mark the step not needed and say so in the note.'],
      ['Generate it', 'Contracts → Vehicle Lease Agreements → pick him and the rego. Check the weekly fee, and set the lease fee, rate and cap on his personnel record so the RCTI deducts the right amount.'],
      ['He signs it in the portal', 'It lands on his My Van tab. The Contract Register shows who has signed and who has not, and there is a reminder button for the ones who have not.'],
      ['Do not let it run unsigned', 'We are deducting a lease fee from his RCTI. That deduction wants a signed agreement behind it from the first week, not from whenever he got around to it.'],
    ],
    done: 'A signed vehicle lease is on the Contract Register — or the step is marked not needed because the van is his.',
  },
  {
    key: 'subcontractor',
    title: 'Present the subcontractor agreement to sign',
    where: 'Compliance & Docs → Contracts → Subcontractor Agreements',
    link: (t) => `/contracts?tab=subcontractor&driver=${encodeURIComponent(t.driverLogin)}`,
    linkLabel: () => 'Open Subcontractor',
    needsLogin: true,
    resolve: (ev) => {
      const c = ev.subcontractor;
      if (c.status === 'signed') return { state: 'done', detail: 'Signed and on the register.' };
      if (c.status === 'pending') return { state: 'progress', detail: 'Presented — waiting for him to review and sign in the portal.' };
      if (c.status === 'declined') return { state: 'todo', detail: 'He declined it — this needs Paul.' };
      return { state: 'todo', detail: 'No agreement presented yet.' };
    },
    help: [
      ['Generate it last, on purpose', 'The agreement carries his rates. Doing it after step 3 means the numbers on the paper are the numbers in the engine, rather than a set that has to be corrected later.'],
      ['Generate it', 'Contracts → Subcontractor Agreements → pick him. The entity and ABN come across from his personnel record — check them against the ABN lookup, not against what he told us.'],
      ['Sole trader or company', 'Either is fine. With no company yet the agreement is made out to him personally against his own ABN — normal, and what most people start on.'],
      ['When he later moves to a company', 'Three things change together and none happen on their own: his personnel record (company name + new ABN), a fresh agreement in the company name, and the bank account we pay. Do it between pay periods — an RCTI is made out to whichever entity is on the record when it is built.'],
      ['He reviews and signs in the portal', 'It is his to read. Do not chase a signature the same day you send it; do chase it before the first period closes.'],
      ['This one gates his pay', 'A parcel rate cannot be activated against an unsigned agreement — the engine blocks it deliberately. An unsigned agreement at period end is a driver who cannot be paid properly.'],
    ],
    done: 'The Contract Register shows his subcontractor agreement signed.',
  },
];

// A hand-tick or a not-needed mark sits on top of the evidence.
function resolveStep(step, task, ev, extra) {
  const manual = (task.steps || {})[step.key];
  const auto = step.resolve(ev, task, extra);
  if (manual && manual.state === 'na') return { ...auto, state: 'na', manual };
  if (manual && manual.state === 'done') return { ...auto, state: 'done', manual };
  if (step.needsLogin && !task.driverLogin) {
    return { state: 'blocked', detail: 'Waiting for his login — finish step 1.' };
  }
  return auto;
}

// ── before you start ──────────────────────────────────────────────────────────
// The only part of the job with no step to hang it on: what to have in your hand
// BEFORE opening the engine. Shown open while the login is still missing — which is
// exactly when it matters — and collapsible once the record exists and it is history.
const PREP = [
  ['The Australia Post login', 'From the AP CMS portal. The one thing we don’t invent.'],
  ['Full legal name, mobile, email, residential address', ''],
  ['An ABN, and whether he’s registered for GST', 'A sole-trader ABN is enough to start. The company name can be blank on day one — but never the ABN, because the RCTI needs it.'],
  ['Bank account name, BSB, account number', ''],
  ['Start date, and which round he’s joining', ''],
  ['Whether he supplies his own van or takes an HPL lease', ''],
];

function BeforeYouStart({ defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      border: '1px solid var(--hp-border)', borderLeft: '3px solid var(--hp-gold)',
      borderRadius: 8, background: 'var(--hp-bg)', marginBottom: 6, overflow: 'hidden',
    }}>
      <button onClick={() => setOpen(o => !o)} style={{
        ...btn, width: '100%', border: 'none', background: 'none', justifyContent: 'flex-start',
        padding: '11px 13px', fontWeight: 600,
      }}>
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        Before you start — have these in your hand
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px 34px', display: 'grid', gap: 9 }}>
          <div style={{ fontSize: '0.83rem', ...dim }}>
            Don’t open the engine until you have all of this. Half a record is worse than none —
            the compliance board, the rate engine and the RCTI all start counting from the moment
            the row exists.
          </div>
          <ul style={{ margin: 0, paddingLeft: 16, display: 'grid', gap: 6 }}>
            {PREP.map(([head, sub]) => (
              <li key={head} style={{ fontSize: '0.84rem' }}>
                {head}
                {sub && <div style={{ fontSize: '0.78rem', ...dim, marginTop: 2 }}>{sub}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── create the driver, inside step 1 ──────────────────────────────────────────
// The first version of this page had no way to DO step one: it offered a box that
// wanted a login, refused anything not already in the engine, and sent you to a
// twenty-five field form on another page. That is not a step, it is a locked door.
// So the record is created here, in the task, from the few fields it actually needs —
// everything else is asked for later by steps 2 and 6, which exist for exactly that.
const ROUTES = ['City', 'Broadway', 'Darlinghurst'];
const TYPES = ['Parcel', 'Hourly', 'None'];

// Once the login is attached, finish what Personnel's "Activate as Driver" does, so the
// two routes leave the same trail: the applicant card turns Hired against this login, and
// the documents he gave at interview / on his onboarding link are filed into Compliance.
// Without this the applicant stayed "Applicant", and activating him from there went
// straight into "Driver already exists". Then refresh Personnel so he shows up in search.
async function closeOutApplicant(task, login, addNotification, reloadPersonnel) {
  try {
    if (task.applicantId) {
      const hired = await post('/.netlify/functions/applicants-sb', {
        action: 'saveApplicant', id: task.applicantId, markHired: true, driverLogin: login,
      });
      if (!hired.alreadyHired) {
        const imp = await post(DMS_API, { action: 'importFromApplicant', applicantId: task.applicantId, driverLogin: login });
        if ((imp.imported || []).length) addNotification(`Compliance seeded from the applicant record: ${imp.imported.join(', ')}`, 'success');
      }
    }
  } catch (e) {
    addNotification(`${login} is set on the task, but updating the applicant record didn't go through: ${e.message}`, 'error');
  } finally {
    if (reloadPersonnel) reloadPersonnel();
  }
}

function CreateDriverInline({ task, onDone, addNotification }) {
  const { reloadPersonnel } = useApp();
  const [open, setOpen] = useState(false);
  const [linking, setLinking] = useState(false);   // the "already made him" path
  const [linkDraft, setLinkDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [madePin, setMadePin] = useState('');
  const [f, setF] = useState({
    login: '', name: task.personName || '', mobile: '', email: '',
    pin: '', abn: '', route: 'Darlinghurst', type: 'Parcel',
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const create = async () => {
    if (!f.login.trim()) { addNotification('Enter the login Australia Post issued.', 'error'); return; }
    if (!f.name.trim()) { addNotification('Enter his full name.', 'error'); return; }
    const login = f.login.trim().toUpperCase();
    setBusy(true);
    // Two writes, and they fail differently. Creating the driver is the one that
    // matters and cannot be repeated — "Driver already exists" on a second attempt is
    // exactly the dead end this whole screen exists to remove. So if the record is made
    // and only the LINK fails, say so, show the PIN anyway, and leave her one button.
    let pin = '';
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'addDriver',
          driver: {
            login, name: f.name.trim(),
            mobile: f.mobile.trim(), email: f.email.trim(), pin: f.pin.trim(),
            abn: f.abn.trim(), route: f.route, type: f.type,
            gstRegistered: 'Yes', vehicleType: 'Own Vehicle',
          },
        }),
      }).then(r => r.json());
      if (!res.success) throw new Error(res.error || 'Could not create the driver');
      pin = res.pin || '';
    } catch (e) {
      addNotification(e.message, 'error');
      setBusy(false);
      return;
    }
    try {
      await post(API, { action: 'setTaskLogin', id: task.id, driverLogin: login });
      setMadePin(pin || ' ');
      addNotification(`${login} created${pin ? ` — portal PIN ${pin}` : ''}`, 'success');
      await closeOutApplicant(task, login, addNotification, reloadPersonnel);
    } catch (e) {
      // The driver EXISTS. Do not let her think it failed and try again.
      setMadePin(pin || ' ');
      setLinkDraft(login);
      setLinking(true);
      setOpen(false);
      addNotification(`${login} was created${pin ? ` (PIN ${pin})` : ''}, but attaching him to this task didn't go through: ${e.message} — press Link to finish it.`, 'error');
    } finally {
      setBusy(false);
      await onDone();
    }
  };

  const link = async () => {
    setBusy(true);
    try {
      await post(API, { action: 'setTaskLogin', id: task.id, driverLogin: linkDraft.trim() });
      addNotification(`Linked to ${linkDraft.trim().toUpperCase()}`, 'success');
      await closeOutApplicant(task, linkDraft.trim().toUpperCase(), addNotification, reloadPersonnel);
      await onDone();
    } catch (e) { addNotification(e.message, 'error'); } finally { setBusy(false); }
  };

  if (madePin && !linking) {
    return (
      <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 8, background: 'rgba(34,197,94,0.10)', border: '1px solid var(--hp-green, #16a34a)' }}>
        <div style={{ fontWeight: 600, fontSize: '0.86rem', marginBottom: 3 }}>Driver created.</div>
        <div style={{ fontSize: '0.83rem' }}>
          His portal PIN is <b style={{ ...mono, fontSize: '0.95rem' }}>{madePin}</b> — give him this with his login.
          He signs in at <b>hollypaul.com/driver-log</b>.
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      {!open && !linking && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={btnPrimary} onClick={() => setOpen(true)}>
            <UserPlus size={14} /> Create his driver record
          </button>
          <button style={{ ...btnSm, border: 'none', background: 'none', color: 'var(--hp-gold)' }}
            onClick={() => setLinking(true)}>
            Already created him? Link the login
          </button>
        </div>
      )}

      {linking && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 190 }}>
            <label style={label}>Existing login</label>
            <input style={{ ...field, ...mono }} placeholder="e.g. SMITHM2" value={linkDraft}
              onChange={e => setLinkDraft(e.target.value.toUpperCase())} />
          </div>
          <button style={btnPrimary} disabled={busy || !linkDraft.trim()} onClick={link}>Link</button>
          <button style={btnSm} onClick={() => setLinking(false)}>Back</button>
        </div>
      )}

      {open && (
        <div style={{ padding: '14px 16px', borderRadius: 8, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', display: 'grid', gap: 12 }}>
          <div style={{ fontSize: '0.78rem', ...dim }}>
            Just enough to exist and sign in. Documents, prices, streets and the rest are
            steps 2 to 8 — you don’t need them now.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={label}>Australia Post login *</label>
              <input style={{ ...field, ...mono }} placeholder="e.g. SMITHM2" value={f.login}
                onChange={e => set('login', e.target.value.toUpperCase())} />
            </div>
            <div>
              <label style={label}>Full name *</label>
              <input style={field} value={f.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div>
              <label style={label}>Mobile</label>
              <input style={field} placeholder="04xx xxx xxx" value={f.mobile} onChange={e => set('mobile', e.target.value)} />
            </div>
            <div>
              <label style={label}>Email</label>
              <input style={field} value={f.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div>
              <label style={label}>Route</label>
              <select style={field} value={f.route} onChange={e => set('route', e.target.value)}>
                {ROUTES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Type</label>
              <select style={field} value={f.type} onChange={e => set('type', e.target.value)}>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>ABN</label>
              <input style={field} placeholder="Sole trader is fine" value={f.abn} onChange={e => set('abn', e.target.value)} />
            </div>
            <div>
              <label style={label}>Portal PIN</label>
              <input style={field} inputMode="numeric" placeholder="Blank = generate one" value={f.pin}
                onChange={e => set('pin', e.target.value.replace(/[^0-9]/g, ''))} />
            </div>
          </div>
          {!f.abn.trim() && (
            <div style={{ fontSize: '0.76rem', color: '#b45309' }}>
              No ABN yet? You can still create him — but the RCTI needs one before he is paid,
              and he will show under “No ABN” on Personnel until it is there.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button style={btnSm} onClick={() => setOpen(false)}>Cancel</button>
            <button style={btnPrimary} disabled={busy} onClick={create}>
              {busy ? 'Creating…' : 'Create and link'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── one step ──────────────────────────────────────────────────────────────────
function StepRow({ n, step, task, ev, extra, onSetStep, onChanged, addNotification, busy }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noting, setNoting] = useState(false);
  const { state, detail, manual } = resolveStep(step, task, ev, extra);
  const s = STATE[state];
  const blocked = state === 'blocked';

  // Step 2 and 5 both talk about documents; show the actual list so "a tick for
  // completed and a required for not completed" is on screen, not implied.
  const docList = (step.key === 'documents' || step.key === 'compliance') && extra.docs
    ? extra.docs.required.map((k) => ({
        key: k,
        label: extra.docs.labels[k] || k,
        status: extra.docs.items[k]?.status || 'missing',
      }))
    : null;

  return (
    <div style={{ borderTop: '1px solid var(--hp-border)', padding: '14px 0', opacity: blocked ? 0.62 : 1 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
          background: state === 'done' ? 'var(--hp-green, #16a34a)' : 'var(--hp-surface)',
          border: `1px solid ${state === 'done' ? 'var(--hp-green, #16a34a)' : 'var(--hp-border)'}`,
          color: state === 'done' ? '#fff' : 'var(--hp-dim)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', fontWeight: 700, ...mono,
        }}>
          {state === 'done' ? <Check size={14} /> : n}
        </div>

        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>{step.title}</span>
            <Pill state={state} />
            {manual && (
              <span style={{ fontSize: '0.7rem', ...dim }} title={manual.note || ''}>
                {manual.state === 'na' ? 'marked not needed' : 'ticked by hand'} · {manual.by}
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.78rem', ...dim, marginTop: 3 }}>{detail}</div>
          {manual?.note && (
            <div style={{ fontSize: '0.76rem', color: 'var(--hp-text)', marginTop: 4, fontStyle: 'italic' }}>“{manual.note}”</div>
          )}
          <div style={{ fontSize: '0.72rem', ...dim, marginTop: 4, ...mono }}>{step.where}</div>

          {/* Step 1 is the only step whose work belongs on this page: without a driver
              record nothing below it can even be attempted. */}
          {step.key === 'login' && !task.driverLogin && (
            <CreateDriverInline task={task} onDone={onChanged} addNotification={addNotification} />
          )}

          {docList && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 9 }}>
              {docList.map((d) => {
                const ok = d.status === 'ok';
                const asked = d.status === 'renewal';
                return (
                  <span key={d.key} style={{
                    fontSize: '0.72rem', padding: '3px 8px', borderRadius: 6,
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: ok ? 'rgba(34,197,94,0.13)' : asked ? 'rgba(59,130,246,0.13)' : 'rgba(245,158,11,0.15)',
                    color: ok ? '#16a34a' : asked ? '#2563eb' : '#b45309',
                  }}>
                    {ok ? <Check size={11} /> : <AlertCircle size={11} />}
                    {d.label}
                    {!ok && <span style={{ opacity: 0.75 }}>· {asked ? 'requested' : d.status === 'expired' ? 'expired' : d.status === 'expiring' ? 'expiring' : 'required'}</span>}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <button style={btnSm} onClick={() => setOpen((o) => !o)} title="What to do on this step">
            <HelpCircle size={13} /> Help with this task
          </button>
          {step.link(task) && (
            <button style={btnSm} disabled={blocked}
              onClick={() => navigate(step.link(task))}>
              <ExternalLink size={13} /> {step.linkLabel(task)}
            </button>
          )}
        </div>
      </div>

      {open && (
        <div style={{
          marginTop: 12, marginLeft: 38, padding: '14px 16px', borderRadius: 8,
          background: 'var(--hp-bg)', border: '1px solid var(--hp-border)',
          borderLeft: '3px solid var(--hp-gold)', display: 'grid', gap: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span style={{ ...label, marginBottom: 0 }}>How to do step {n}</span>
            <button style={{ ...btnSm, padding: '2px 6px' }} onClick={() => setOpen(false)}><X size={12} /></button>
          </div>
          {step.help.map(([h, body]) => (
            <div key={h}>
              <div style={{ fontWeight: 600, fontSize: '0.84rem', marginBottom: 2 }}>{h}</div>
              <div style={{ fontSize: '0.83rem', ...dim, lineHeight: 1.55 }}>{body}</div>
            </div>
          ))}
          <div style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 10, fontSize: '0.83rem' }}>
            <span style={{ color: 'var(--hp-green, #16a34a)', fontWeight: 600 }}>Done when </span>
            <span style={dim}>{step.done}</span>
          </div>

          <div style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 10, display: 'grid', gap: 8 }}>
            <span style={{ ...label, marginBottom: 0 }}>If the engine can’t see it</span>
            <div style={{ fontSize: '0.8rem', ...dim }}>
              The tick above comes from the data. Override it only when the step really is done or really doesn’t
              apply — the override is stamped with your name and shown next to the step.
            </div>
            {noting ? (
              <div style={{ display: 'grid', gap: 8 }}>
                <input style={field} placeholder="Why — e.g. he supplies his own van" value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)} />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button style={btnSm} disabled={busy}
                    onClick={() => { onSetStep(step.key, 'done', noteDraft); setNoting(false); setNoteDraft(''); }}>
                    Mark done
                  </button>
                  <button style={btnSm} disabled={busy}
                    onClick={() => { onSetStep(step.key, 'na', noteDraft); setNoting(false); setNoteDraft(''); }}>
                    Not needed
                  </button>
                  <button style={btnSm} onClick={() => { setNoting(false); setNoteDraft(''); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button style={btnSm} onClick={() => setNoting(true)} disabled={busy}>Override…</button>
                {manual && (
                  <button style={btnSm} disabled={busy} onClick={() => onSetStep(step.key, 'clear', '')}>
                    Remove override
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── one task ──────────────────────────────────────────────────────────────────
function TaskCard({ task, extraFor, me, onChanged, onEdit, addNotification }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loginDraft, setLoginDraft] = useState('');
  // A task always comes back with evidence attached; default it anyway so a partial
  // response degrades to "nothing done yet" rather than a blank page.
  const ev = task.evidence || { driver: {}, routes: {}, vehicle: {}, lease: {}, subcontractor: {} };
  const extra = extraFor(task.driverLogin);

  const resolved = STEPS.map((s) => resolveStep(s, task, ev, extra));
  const doneCount = resolved.filter((r) => r.state === 'done' || r.state === 'na').length;
  const allDone = doneCount === STEPS.length;
  const mine = (task.assignedTo || '').toUpperCase() === (me || '').toUpperCase();

  const act = async (body, msg) => {
    setBusy(true);
    try {
      await post(API, { ...body, id: task.id });
      if (msg) addNotification(msg, 'success');
      await onChanged();
    } catch (e) { addNotification(e.message, 'error'); } finally { setBusy(false); }
  };

  return (
    <div style={{ ...card, marginBottom: 18, overflow: 'hidden' }}>
      {/* header */}
      <div style={{ padding: '14px 16px', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => setOpen((o) => !o)} style={{ ...btn, padding: 4, border: 'none', background: 'none' }}>
          {open ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
        </button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>Onboard {task.personName}</span>
            {task.driverLogin
              ? <span style={{ ...mono, fontSize: '0.78rem', color: 'var(--hp-gold)' }}>{task.driverLogin}</span>
              : <span style={{ fontSize: '0.74rem', color: '#b45309' }}>login not set</span>}
            {task.status === 'done' && <Pill state="done" />}
            {task.pausedAt && task.status !== 'done' && <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--hp-border)', ...dim }}>Paused</span>}
          </div>
          <div style={{ fontSize: '0.76rem', ...dim, marginTop: 3 }}>
            {task.assignedTo === me ? 'Assigned to you' : `Assigned to ${task.assignedTo}`}
            {task.startDate ? ` · starts ${task.startDate}` : ''}
            {task.acceptedAt ? ` · accepted ${new Date(task.acceptedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}` : ' · not accepted yet'}
          </div>
          {task.note && <div style={{ fontSize: '0.8rem', marginTop: 5 }}>{task.note}</div>}
          {task.applicantId && (
            <button style={{ ...btnSm, padding: 0, border: 'none', background: 'none', color: 'var(--hp-gold)', marginTop: 5 }}
              onClick={() => navigate(`/personnel?tab=applicants&applicant=${encodeURIComponent(task.applicantId)}`)}>
              From the applicant record <ExternalLink size={11} />
            </button>
          )}
        </div>

        {/* progress */}
        <div style={{ minWidth: 150 }}>
          <div style={{ fontSize: '0.74rem', ...dim, marginBottom: 4, textAlign: 'right' }}>
            {doneCount} of {STEPS.length} done
          </div>
          <div style={{ height: 6, borderRadius: 4, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', overflow: 'hidden' }}>
            <div style={{
              width: `${(doneCount / STEPS.length) * 100}%`, height: '100%',
              background: allDone ? 'var(--hp-green, #16a34a)' : 'var(--hp-gold)', transition: 'width .3s',
            }} />
          </div>
        </div>

        {task.status !== 'done' && (
          <button style={btnSm} disabled={busy}
            onClick={() => act({ action: 'pauseTask', resume: !!task.pausedAt }, task.pausedAt ? 'Task resumed' : 'Task paused')}
            title={task.pausedAt ? 'Put it back on the list' : 'Park it — nothing is lost, it just stops counting as work'}>
            {task.pausedAt ? 'Resume' : 'Pause'}
          </button>
        )}
        <button style={btnSm} onClick={() => onEdit(task)} title="Edit the name, start date, note or who it's assigned to">
          <Pencil size={13} />
        </button>
        <button style={{ ...btnSm, color: 'var(--hp-red, #dc2626)' }} disabled={busy}
          onClick={() => { if (window.confirm(`Delete the onboarding task for ${task.personName}? The driver record and everything else stays.`)) act({ action: 'deleteTask' }, 'Task deleted'); }}>
          <Trash2 size={13} />
        </button>
      </div>

      {task.pausedAt && task.status !== 'done' && (
        <div style={{ margin: '0 16px 14px', padding: '10px 14px', borderRadius: 8, background: 'var(--hp-bg)', border: '1px dashed var(--hp-border)', fontSize: '0.84rem', ...dim }}>
          Paused{task.pausedBy ? ` by ${task.pausedBy}` : ''} on {new Date(task.pausedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}. Nothing on it is lost — press Resume to pick it up again.
        </div>
      )}

      {/* accept */}
      {task.status === 'open' && !task.pausedAt && (
        <div style={{
          margin: '0 16px 14px', padding: '12px 14px', borderRadius: 8,
          background: 'rgba(201,168,76,0.08)', border: '1px solid var(--hp-gold)',
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <ClipboardCheck size={17} color="var(--hp-gold)" />
          <div style={{ flex: 1, minWidth: 200, fontSize: '0.85rem' }}>
            {mine
              ? <>This task is yours. Accepting it says you have picked it up — the steps below then run in order.</>
              : <>Waiting for {task.assignedTo} to accept.</>}
          </div>
          {mine && (
            <button style={btnPrimary} disabled={busy} onClick={() => act({ action: 'acceptTask' }, 'Task accepted — work down the steps')}>
              <Check size={14} /> Accept this task
            </button>
          )}
        </div>
      )}

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          <BeforeYouStart defaultOpen={!task.driverLogin} />

          {STEPS.map((s, i) => (
            <StepRow key={s.key} n={i + 1} step={s} task={task} ev={ev} extra={extra} busy={busy}
              onChanged={onChanged} addNotification={addNotification}
              onSetStep={(stepKey, state, note) => act({ action: 'setStep', step: stepKey, state, note })} />
          ))}

          <div style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 14, marginTop: 4, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {task.status === 'done' ? (
              <>
                <span style={{ fontSize: '0.83rem', color: 'var(--hp-green, #16a34a)', fontWeight: 600 }}>
                  Closed {task.completedAt ? new Date(task.completedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                </span>
                <button style={btnSm} disabled={busy} onClick={() => act({ action: 'completeTask', reopen: true }, 'Task reopened')}>Reopen</button>
              </>
            ) : (
              <>
                <span style={{ fontSize: '0.83rem', ...dim }}>
                  {allDone ? 'Every step is done — close the task and tell Paul.' : `${STEPS.length - doneCount} step${STEPS.length - doneCount === 1 ? '' : 's'} to go.`}
                </span>
                <button style={allDone ? btnPrimary : btnSm} disabled={busy}
                  onClick={() => act({ action: 'completeTask' }, 'Onboarding closed')}>
                  Close this task
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── set or edit a task ────────────────────────────────────────────────────────
// One form for both. A task outlives its assumptions — a driver named in the note
// leaves, the round changes — so the details have to be correctable in place.
function TaskModal({ task, onClose, onSaved, addNotification }) {
  const editing = !!task;
  const [f, setF] = useState({
    personName: task?.personName || '',
    driverLogin: task?.driverLogin || '',
    assignedTo: task?.assignedTo || 'BOUVENGJ',
    startDate: task?.startDate || '',
    note: task?.note || '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const create = async () => {
    setBusy(true);
    try {
      if (editing) {
        const { driverLogin, ...rest } = f;   // the login is changed by its own Link control
        await post(API, { action: 'updateTask', id: task.id, ...rest });
        addNotification('Task updated', 'success');
      } else {
        await post(API, { action: 'createTask', ...f });
        addNotification(`Onboarding task set for ${f.personName}`, 'success');
      }
      await onSaved();
      onClose();
    } catch (e) { addNotification(e.message, 'error'); } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ ...card, width: 460, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{editing ? `Onboarding — ${task.personName}` : 'Set an onboarding task'}</h3>
          <button style={{ ...btnSm, padding: '3px 7px' }} onClick={onClose}><X size={14} /></button>
        </div>
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label style={label}>Who is starting *</label>
            <input style={field} placeholder="e.g. Massimo" value={f.personName} onChange={(e) => set('personName', e.target.value)} />
          </div>
          {!editing && (
            <div>
              <label style={label}>Australia Post login (if known)</label>
              <input style={{ ...field, ...mono }} placeholder="Leave blank — step 1 sets it" value={f.driverLogin}
                onChange={(e) => set('driverLogin', e.target.value.toUpperCase())} />
              <div style={{ fontSize: '0.72rem', ...dim, marginTop: 4 }}>
                AP issues the login. Normally it is not known yet, and the task starts without one.
              </div>
            </div>
          )}
          <div>
            <label style={label}>Assign to *</label>
            <select style={field} value={f.assignedTo} onChange={(e) => set('assignedTo', e.target.value)}>
              <option value="BOUVENGJ">Jayda (BOUVENGJ)</option>
              <option value="BOUVENGP2">Paul (BOUVENGP2)</option>
            </select>
          </div>
          <div>
            <label style={label}>Start date</label>
            <input style={field} type="date" value={f.startDate} onChange={(e) => set('startDate', e.target.value)} />
          </div>
          <div>
            <label style={label}>Note</label>
            <textarea style={{ ...field, minHeight: 70, resize: 'vertical' }}
              placeholder="Anything the person doing this needs to know — the round, the training arrangement, who to copy rates from"
              value={f.note} onChange={(e) => set('note', e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
            <button style={btn} onClick={onClose}>Cancel</button>
            <button style={btnPrimary} disabled={busy || !f.personName.trim()} onClick={create}>
              {busy ? 'Saving…' : editing ? 'Save' : 'Set the task'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
export default function OnboardingTasksPage() {
  const { currentUser, sheetData, sheetLoading, addNotification } = useApp();
  const isAdmin = currentUser?.role === 'admin';
  const me = (currentUser?.login || '').toUpperCase();

  const [tasks, setTasks] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const d = await post(API, { action: 'listTasks' });
      setTasks(d.tasks || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);

  // Document statuses come from the Compliance page's own call so the two can never
  // disagree. `includeInactive` matters: documents are collected BEFORE somebody
  // starts, which is exactly when this page is used.
  const loadCompliance = useCallback(async () => {
    try {
      const d = await post(DMS_API, { action: 'listCompliance', audience: 'driver', includeInactive: true });
      const labels = {};
      (d.types || []).forEach((t) => { labels[t.key] = t.label; });
      const byLogin = {};
      (d.drivers || []).forEach((r) => { byLogin[(r.login || '').toUpperCase()] = { ...r, labels }; });
      setCompliance(byLogin);
    } catch (e) {
      // A compliance hiccup must not blank the whole page — the other six steps still read.
      setCompliance({});
    }
  }, []);

  useEffect(() => { if (isAdmin) { load(); loadCompliance(); } }, [isAdmin, load, loadCompliance]);

  // Service rates live on the Rates sheet, already in app context.
  const ratesFor = useCallback((login) => {
    const map = sheetData?.ratesMap;
    if (!map || !login) return null;
    return map[login] || map[login.toUpperCase()] || null;
  }, [sheetData]);

  const extraFor = useCallback((login) => {
    const lg = (login || '').toUpperCase();
    const row = lg && compliance ? compliance[lg] : null;
    return {
      rates: ratesFor(lg),
      ratesLoading: !sheetData && sheetLoading,
      ratesUnavailable: !sheetData && !sheetLoading,
      docs: row ? { items: row.items || {}, required: row.required || [], labels: row.labels || {} } : null,
    };
  }, [compliance, ratesFor, sheetData, sheetLoading]);

  const [openTasks, doneTasks] = useMemo(() => {
    const list = tasks || [];
    return [list.filter((t) => t.status !== 'done'), list.filter((t) => t.status === 'done')];
  }, [tasks]);

  if (!isAdmin) {
    return (
      <>
        <div className="page-header"><div className="page-header-left"><h2>Onboarding</h2></div></div>
        <div className="page-body">
          <div className="card" style={{ padding: 28 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Admins only</div>
            <div style={{ ...dim, fontSize: '.88rem' }}>Onboarding tasks are restricted to Paul and Jayda.</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Onboarding</h2>
          <p>Bringing a new driver on, one step at a time — the engine checks each one off as it happens</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={btn} onClick={() => { load(); loadCompliance(); }} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button style={btnPrimary} onClick={() => setAdding(true)}>
            <UserPlus size={14} /> Set a task
          </button>
        </div>
      </div>

      <div className="page-body">
        {error && (
          <div className="card" style={{ padding: 16, marginBottom: 16, borderLeft: '3px solid var(--hp-red, #dc2626)' }}>
            <div style={{ fontWeight: 600, marginBottom: 3 }}>Could not load the tasks</div>
            <div style={{ fontSize: '0.85rem', ...dim }}>{error}</div>
          </div>
        )}

        {loading && !tasks && <div className="card" style={{ padding: 28, textAlign: 'center', ...dim }}>Loading…</div>}

        {tasks && !openTasks.length && !doneTasks.length && (
          <div className="card" style={{ padding: 34, textAlign: 'center' }}>
            <UserPlus size={22} color="var(--hp-gold)" />
            <div style={{ fontWeight: 700, marginTop: 10, marginBottom: 4 }}>No one is being onboarded</div>
            <div style={{ fontSize: '0.86rem', ...dim, maxWidth: 460, margin: '0 auto' }}>
              When somebody new starts, set the task here and assign it. Whoever it goes to accepts it and works
              down the eight steps, with the engine checking each one off as the evidence appears.
            </div>
          </div>
        )}

        {openTasks.map((t) => (
          <TaskCard key={t.id} task={t} extraFor={extraFor} me={me} onChanged={load} onEdit={setEditing} addNotification={addNotification} />
        ))}

        {!!doneTasks.length && (
          <div style={{ marginTop: 10 }}>
            <button style={btnSm} onClick={() => setShowDone((s) => !s)}>
              {showDone ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              {doneTasks.length} closed
            </button>
            {showDone && (
              <div style={{ marginTop: 12 }}>
                {doneTasks.map((t) => (
                  <TaskCard key={t.id} task={t} extraFor={extraFor} me={me} onChanged={load} onEdit={setEditing} addNotification={addNotification} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {adding && <TaskModal onClose={() => setAdding(false)} onSaved={load} addNotification={addNotification} />}
      {editing && <TaskModal task={editing} onClose={() => setEditing(null)} onSaved={load} addNotification={addNotification} />}
    </>
  );
}
